import dayjs from 'dayjs'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

// 导入API

// 权限配置映射类型
interface AuthPermission {
  available?: string // 可用版本: starter, pro, enterprise
  limited?: boolean // 是否受限
  allowed_date_range?: number // 允许的日期范围（天数）
  allowed_day_range?: {
    from: number
    to: number
  }
  allow_types?: string[] // 允许的类型
  limited_number?: number // 限制数量
  add_on?: boolean // 是否为插件
}

interface AuthMap {
  [key: string]: AuthPermission
}

export const useAuthStore = defineStore('auth', () => {
  // 状态
  const authMap = ref<AuthMap>({})

  // 计算属性 - 获取特定功能的权限信息
  const getPermission = computed(() => {
    return (key: string): AuthPermission => {
      return authMap.value[key] || {}
    }
  })

  // 计算属性 - 获取升级文本
  const getAvailableText = computed(() => {
    return (key: string, checkAllowedType = ''): string => {
      const availableVersion: Record<string, string> = {
        pro: 'Pro',
        enterprise: 'Enterprise',
        starter: 'Starter',
      }

      const permission = authMap.value[key] || {}
      const availableKey = permission.available || ''
      const availableText = availableVersion[availableKey] || availableKey

      if (checkAllowedType && Array.isArray(permission.allow_types) && permission.allow_types.includes(checkAllowedType)) {
        // allow_types中的类型无需升级即可使用
        return ''
      }

      if (!permission.available) {
        return ''
      }

      // 如果有权限要求，返回升级文本
      return availableText
    }
  })

  // 计算属性 - 获取lead_attribution权限
  const leadAttribution = computed(() => {
    return authMap.value?.lead_attribution || {}
  })

  // 计算属性 - 获取short_link权限
  const shortLink = computed(() => {
    return authMap.value?.short_link || {}
  })

  // 辅助函数 - 生成日期范围（参考老系统 timezone.js 的实现）
  const dateRangeByDay = (days: number, last = false, timezone?: string) => {
    // last = true 表示不包含今天，结束日期为昨天
    // last = false 表示包含今天，结束日期为今天
    const now = timezone ? dayjs().tz(timezone) : dayjs()
    if (days === 1) {
      // 只允许今天
      const start = now.startOf('day')
      const end = now.endOf('day')
      return [
        start.format('YYYY-MM-DD 00:00:00'),
        end.format('YYYY-MM-DD 23:59:59'),
      ]
    }
    const end = last
      ? now.subtract(1, 'day').endOf('day')
      : now.endOf('day')
    const start = now.startOf('day').subtract(last ? days : days - 1, 'day')
    return [
      start.format('YYYY-MM-DD 00:00:00'),
      end.format('YYYY-MM-DD 23:59:59'),
    ]
  }

  const dateByDay = (offset: number, last = false, options?: { formatter?: string, timezone?: string }) => {
    const formatter = options?.formatter || 'YYYY-MM-DD HH:mm:ss'
    const timezone = options?.timezone
    const now = timezone ? dayjs().tz(timezone) : dayjs()
    const start = now.startOf('day').subtract(last ? offset : offset - 1, 'day')
    return start.format(formatter)
  }

  // Actions
  const setAuthMap = (newAuthMap: AuthMap) => {
    authMap.value = newAuthMap
  }

  // 获取权限规则
  // const getAuthRule = async () => {
  //   try {
  //     const response = await teamAuthRuleApi()
  //     if (response.code > 0 && response.data?.auth_map) {
  //       setAuthMap(response.data.auth_map)
  //     }
  //   }
  //   catch (error) {
  //     console.error('Failed to fetch auth rules:', error)
  //   }
  // }

  // 获取允许的日期范围
  const getAllowedDateRange = (key: string, timezone?: string) => {
    const permission = authMap.value[key] || {}
    let dateRangeValue: any = {}

    if (permission.allowed_day_range && permission.allowed_day_range.from !== undefined && permission.allowed_day_range.to !== undefined) {
      // 具体指定某一天的数据
      const start = dateByDay(0 - permission.allowed_day_range.from, false, { formatter: 'YYYY-MM-DD 00:00:00', timezone })
      const end = dateByDay(0 - permission.allowed_day_range.to, false, { formatter: 'YYYY-MM-DD 23:59:59', timezone })
      dateRangeValue = {
        defaultDate: [start, end],
        dateRange: [start, end],
        planText: 'the special day',
      }
    }
    else if (permission.allowed_date_range && permission.allowed_date_range > 0) {
      // 指定日期范围
      dateRangeValue = {
        defaultDate: dateRangeByDay(permission.allowed_date_range, false, timezone),
        dateRange: dateRangeByDay(permission.allowed_date_range, false, timezone),
        planText: '',
      }
      if (permission.available) {
        dateRangeValue.planText = `last ${permission.allowed_date_range} days`
      }
    }
    return dateRangeValue
  }

  // 检查并调整日期范围
  const checkAllowedDateRange = (key: string, dateRange?: string[], timezone?: string) => {
    const permission = authMap.value[key] || {}
    let dateRangeValue = dateRange || []
    let allowedDateRange: string[] = []

    if (permission.allowed_day_range && permission.allowed_day_range.from !== undefined && permission.allowed_day_range.to !== undefined) {
      // 具体指定某一天的数据
      allowedDateRange[0] = dateByDay(0 - permission.allowed_day_range.from, false, { formatter: 'YYYY-MM-DD 00:00:00', timezone })
      allowedDateRange[1] = dateByDay(0 - permission.allowed_day_range.to, false, { formatter: 'YYYY-MM-DD 23:59:59', timezone })
    }
    else if (permission.allowed_date_range && permission.allowed_date_range > 0) {
      // 指定日期范围
      allowedDateRange = dateRangeByDay(permission.allowed_date_range, false, timezone)
    }

    if (!dateRangeValue || dateRangeValue.length < 2) {
      dateRangeValue = allowedDateRange.length > 0 ? allowedDateRange : dateRangeByDay(7, true, timezone)
    }

    if (allowedDateRange.length > 0) {
      if (!dateRangeValue || dateRangeValue.length < 2) {
        dateRangeValue = allowedDateRange
      }
      else {
        if (dateRangeValue[0] < allowedDateRange[0]) {
          dateRangeValue[0] = allowedDateRange[0]
        }
        if (dateRangeValue[1] > allowedDateRange[1] || !dateRangeValue[1]) {
          dateRangeValue[1] = allowedDateRange[1]
        }
      }
    }

    if (dateRangeValue[0] > dateRangeValue[1]) {
      // 转为对应dateRangeValue[0]的23:59:59
      const start = timezone ? dayjs.tz(dateRangeValue[0], timezone) : dayjs(dateRangeValue[0])
      dateRangeValue[1] = start.endOf('day').format('YYYY-MM-DD 23:59:59')
    }

    return dateRangeValue
  }

  return {
    // 状态
    authMap,

    // 计算属性
    getPermission,
    getAvailableText,
    leadAttribution,
    shortLink,

    // Actions
    setAuthMap,
    // getAuthRule,
    getAllowedDateRange,
    checkAllowedDateRange,
  }
})
